import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/layout/AdminLayout";
import { adminApi } from "../../api";
import { useAuth } from "../../context/AuthContext";
import ViewAsButton from "../../components/admin/ViewAsButton";

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div className="dash-kpi-card">
      <div className="dash-kpi-icon" style={{ background: color + "1a", color }}>
        {icon}
      </div>
      <div>
        <div className="dash-kpi-value">{value}</div>
        <div className="dash-kpi-label">{label}</div>
        {sub && <div className="dash-kpi-sub">{sub}</div>}
      </div>
    </div>
  );
}

function VenueAvatar({ venue }) {
  const [broken, setBroken] = useState(false);

  if (venue.logo_url && !broken) {
    return (
      <div
        className="venue-avatar"
        style={{ background: venue.primary_color || "#3b82f6", width: 38, height: 38, borderRadius: 8, flexShrink: 0, padding: 0, overflow: "hidden" }}
      >
        <img
          src={venue.logo_url}
          alt={venue.name}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          onError={() => setBroken(true)}
        />
      </div>
    );
  }

  return (
    <div
      className="venue-avatar"
      style={{ background: venue.primary_color || "#3b82f6", width: 38, height: 38, fontSize: "0.9rem", flexShrink: 0 }}
    >
      {venue.name?.charAt(0)?.toUpperCase()}
    </div>
  );
}

function VenueRow({ venue, memberCount }) {
  return (
    <div className="dash-vrow">
      <VenueAvatar venue={venue} />
      <div className="dash-vrow-info">
        <span className="dash-vrow-name">{venue.name}</span>
        <span className="dash-vrow-slug">/{venue.slug}</span>
      </div>
      <div className="dash-vrow-members">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13" style={{ flexShrink: 0 }}>
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2"/><circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
        </svg>
        {memberCount !== undefined ? memberCount.toLocaleString() : "—"}
      </div>
      <span className={`badge ${venue.active ? "active" : "inactive"}`}>
        <span className={`status-dot ${venue.active ? "active" : "inactive"}`} />
        {venue.active ? "Active" : "Inactive"}
      </span>
      <div className="dash-vrow-actions">
        <Link to={`/admin/venues/${venue.id}`} className="btn btn-primary btn-sm">Manage</Link>
        <a href={`/${venue.slug}`} target="_blank" rel="noreferrer" className="btn btn-success btn-sm">Signup ↗</a>
        <Link to={`/portal/${venue.slug}`} className="btn btn-purple btn-sm">Portal</Link>
        <ViewAsButton venueId={venue.id} />
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [venues, setVenues] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.getVenues().then(async (v) => {
      setVenues(v);
      if (v.length > 0) {
        const results = await Promise.all(
          v.map((venue) =>
            Promise.all([
              adminApi.getMembers(venue.id, { limit: 1, offset: 0 })
                .then((r) => ({ id: venue.id, count: r.total }))
                .catch(() => ({ id: venue.id, count: 0 })),
              adminApi.getLogs(venue.id, { limit: 6, offset: 0 })
                .then((logs) => logs.map((l) => ({ ...l, venueName: venue.name, venueColor: venue.primary_color })))
                .catch(() => []),
            ])
          )
        );
        const counts = {};
        const allLogs = [];
        for (const [countObj, logs] of results) {
          counts[countObj.id] = countObj.count;
          allLogs.push(...logs);
        }
        allLogs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setMemberCounts(counts);
        setRecentLogs(allLogs.slice(0, 8));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const activeCount = venues.filter((v) => v.active).length;
  const totalMembers = Object.values(memberCounts).reduce((s, c) => s + c, 0);
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const recentSignups = recentLogs.filter(
    (l) => new Date(l.created_at).getTime() > sevenDaysAgo && l.status === "success"
  ).length;

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const userName = user?.name || user?.email?.split("@")[0] || "Admin";

  return (
    <AdminLayout>
      {/* Welcome header */}
      <div className="dash-welcome">
        <div>
          <h1 className="dash-greeting">{greeting}, {userName}</h1>
          <p className="page-subtitle">Here's an overview of your membership platform</p>
        </div>
        <Link to="/admin/venues/new" className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Venue
        </Link>
      </div>

      {loading ? (
        <div className="loading-state"><div className="spinner" /></div>
      ) : (
        <>
          {/* KPI stat cards */}
          <div className="dash-kpi-grid">
            <KpiCard
              label="Total Venues"
              value={venues.length}
              sub={venues.length - activeCount > 0 ? `${venues.length - activeCount} inactive` : "All active"}
              color="#3b82f6"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              }
            />
            <KpiCard
              label="Active Venues"
              value={activeCount}
              sub={`${Math.round((activeCount / Math.max(venues.length, 1)) * 100)}% of total`}
              color="#22c55e"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              }
            />
            <KpiCard
              label="Total Members"
              value={totalMembers.toLocaleString()}
              sub="across all venues"
              color="#a855f7"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87" />
                  <path d="M16 3.13a4 4 0 010 7.75" />
                </svg>
              }
            />
            <KpiCard
              label="Recent Signups"
              value={recentSignups}
              sub="last 7 days"
              color="#f59e0b"
              icon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                  <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
              }
            />
          </div>

          {/* Body: venues list + recent activity */}
          <div className="dash-body-grid">
            {/* Venues */}
            <div>
              <div className="dash-section-header">
                <h2>Venues</h2>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  {venues.length > 6 && (
                    <Link to="/admin/venues" className="dash-view-all">View all {venues.length} →</Link>
                  )}
                  <Link to="/admin/clients" className="btn btn-outline btn-sm">Client Users</Link>
                </div>
              </div>
              {venues.length === 0 ? (
                <div className="empty-state">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  <h3>No venues yet</h3>
                  <p>Create your first venue to start accepting memberships</p>
                  <Link to="/admin/venues/new" className="btn btn-primary">Create Venue</Link>
                </div>
              ) : (
                <div className="dash-vrow-list">
                  <div className="dash-vrow-header">
                    <span style={{ flex: "1 1 auto" }}>Venue</span>
                    <span className="dash-vrow-members-hdr">Members</span>
                    <span style={{ width: 80 }}>Status</span>
                    <span style={{ width: 210 }}>Actions</span>
                  </div>
                  {venues.slice(0, 6).map((v) => (
                    <VenueRow key={v.id} venue={v} memberCount={memberCounts[v.id]} />
                  ))}
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div>
              <div className="dash-section-header">
                <h2>Recent Activity</h2>
              </div>
              <div className="dash-activity-panel">
                {recentLogs.length === 0 ? (
                  <div className="dash-activity-empty">No recent activity</div>
                ) : (
                  recentLogs.map((log) => (
                    <div key={log.id} className="dash-activity-item">
                      <div className={`dash-activity-dot ${log.status === "success" ? "success" : "error"}`} />
                      <div className="dash-activity-body">
                        <div className="dash-activity-name">
                          {log.first_name && log.last_name
                            ? `${log.first_name} ${log.last_name}`
                            : log.membership_id || "Unknown member"}
                        </div>
                        <div className="dash-activity-meta">
                          <span
                            className="dash-activity-venue"
                            style={{ "--vcolor": log.venueColor || "#3b82f6" }}
                          >
                            {log.venueName}
                          </span>
                          <span className="dash-activity-action">{log.action}</span>
                        </div>
                      </div>
                      <div className="dash-activity-time">{timeAgo(log.created_at)}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
